import React from "react";
import ReactDOM from "react-dom";

/**
 * Props:
 *   open          boolean
 *   title         string
 *   description   string
 *   warning       string | { title, description }  — shown in amber box
 *   confirmLabel  string   — default "Confirmar"
 *   confirmVariant "danger"|"primary"|"success"|"save"  — default "primary"
 *   cancelLabel   string   — default "Cancelar"
 *   onConfirm     async () => void
 *   onCancel      () => void
 *   children      React node — extra content before action buttons
 */
const ConfirmModal = ({
  open,
  title,
  description,
  warning,
  confirmLabel = "Confirmar",
  confirmVariant = "primary",
  cancelLabel = "Cancelar",
  onConfirm,
  onCancel,
  children,
}) => {
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open) setLoading(false);
  }, [open]);

  if (!open) return null;

  const btnClass = confirmVariant === "danger"   ? "bini-btn-danger"
                 : confirmVariant === "success"  ? "bini-btn-success"
                 : "bini-btn-save";

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm?.();
    } finally {
      setLoading(false);
    }
  };

  const renderWarning = () => {
    if (!warning) return null;
    if (typeof warning === "string") {
      return <div className="bini-warning" style={{ marginBottom: 12 }}>{warning}</div>;
    }
    return (
      <div className="bini-warning" style={{ marginBottom: 12 }}>
        {warning.title && <div className="t">⚠ {warning.title}</div>}
        {warning.description && <div className="d">{warning.description}</div>}
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div
      className="bini-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div className="bini-modal" role="dialog" aria-modal="true">
        <div className="bini-modal-title">{title}</div>
        {description && (
          <div style={{ color: "var(--bini-text-secondary)", fontSize: 11, marginBottom: 10 }}>
            {description}
          </div>
        )}
        {renderWarning()}
        {children}
        <div className="bini-modal-actions">
          <button className="bini-btn bini-btn-cancel" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </button>
          <button className={`bini-btn ${btnClass}`} onClick={handleConfirm} disabled={loading}>
            {loading ? "..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmModal;
